export interface PricingPlan {
  id: string;
  name: string;
  tagline: string;
  price: string;
  priceValue: number;
  priceLabel: string;
  yearlyPrice: number | null;
  yearlyLabel: string | null;
  description: string;
  storage: string;
  features: string[];
  highlighted: boolean;
  badge: string;
  color: 'primary' | 'secondary' | 'accent';
  cta: string;
  requiresApproval: boolean;
  activationLabel: string;
  activationIcon: string;
  hasDomain: boolean;
  hasLives: boolean;
  hasApp: boolean;
}

export const pricingPlans: PricingPlan[] = [
  {
    id: 'free',
    name: 'Free',
    tagline: 'Tu primera frecuencia en la web',
    price: 'Gratis',
    priceValue: 0,
    priceLabel: 'Para siempre',
    yearlyPrice: null,
    yearlyLabel: null,
    description: 'Arrancá tu presencia digital con lo esencial. Perfil profesional, stream en vivo y cabina básica sin costo.',
    storage: '1 GB',
    features: [
      'Perfil básico profesional',
      'Stream URL para transmitir',
      'Cabina básica con chat',
      'Sección de contacto',
      'Redes sociales integradas',
      '1 GB almacenamiento — solo datos ligeros',
      'Sin fotos',
      'Sin videos',
      'Sin live',
    ],
    highlighted: false,
    badge: 'Para empezar',
    color: 'secondary',
    cta: 'Comenzar Gratis',
    requiresApproval: true,
    activationLabel: 'Sujeto a aprobación',
    activationIcon: 'ri-time-line',
    hasDomain: false,
    hasLives: false,
    hasApp: false,
  },
  {
    id: 'starter',
    name: 'Starter',
    tagline: 'Un paso más allá del free',
    price: '$12/mes',
    priceValue: 12,
    priceLabel: 'Facturación mensual',
    yearlyPrice: 120,
    yearlyLabel: '$120/año (2 meses gratis)',
    description: 'Más almacenamiento, fotos y publicaciones simples para empezar a construir tu comunidad.',
    storage: '10 GB',
    features: [
      'Todo lo de Free',
      'Fotos (hasta 500)',
      'Galerías básicas (3)',
      'Publicaciones tipo blog',
      'Cabina con chat completo',
      '2 locutores',
      'Estadísticas básicas',
      'Sin dominio personalizado',
      'Sin lives',
      'Sin app individual',
    ],
    highlighted: false,
    badge: 'Crecimiento',
    color: 'secondary',
    cta: 'Elegir Starter',
    requiresApproval: false,
    activationLabel: 'Activación inmediata',
    activationIcon: 'ri-flashlight-line',
    hasDomain: false,
    hasLives: false,
    hasApp: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'El plan más elegido por las radios',
    price: '$29/mes',
    priceValue: 29,
    priceLabel: 'Facturación mensual',
    yearlyPrice: 290,
    yearlyLabel: '$290/año (2 meses gratis)',
    description: 'Activación inmediata. Fotos ilimitadas, comunidad y dominio propio para hacer crecer tu audiencia.',
    storage: '50 GB',
    features: [
      'Activación inmediata',
      'Fotos ilimitadas',
      'Galerías de fotos por categoría',
      'Publicaciones tipo Facebook',
      'Comunidad de oyentes',
      'Dominio personalizado',
      '5 locutores',
      'Estadísticas avanzadas',
      'Sin publicidad',
    ],
    highlighted: true,
    badge: 'Más popular',
    color: 'primary',
    cta: 'Elegir Pro',
    requiresApproval: false,
    activationLabel: 'Activación inmediata',
    activationIcon: 'ri-flashlight-line',
    hasDomain: true,
    hasLives: false,
    hasApp: false,
  },
  {
    id: 'business',
    name: 'Business',
    tagline: 'Para radios con equipo grande',
    price: '$89/mes',
    priceValue: 89,
    priceLabel: 'Facturación mensual',
    yearlyPrice: 890,
    yearlyLabel: '$890/año (2 meses gratis)',
    description: 'Lives, videos, dominio y app individual. Todo lo que un equipo profesional necesita.',
    storage: '200 GB',
    features: [
      'Todo lo de Pro',
      'Videos bajo demanda',
      'Lives y transmisiones en directo',
      'App individual personalizada',
      'Dominio personalizado',
      'Locutores ilimitados',
      'Analytics avanzado',
      'Soporte prioritario',
      'API de acceso',
    ],
    highlighted: false,
    badge: 'Equipos',
    color: 'accent',
    cta: 'Elegir Business',
    requiresApproval: false,
    activationLabel: 'Activación inmediata',
    activationIcon: 'ri-flashlight-line',
    hasDomain: true,
    hasLives: true,
    hasApp: true,
  },
  {
    id: 'premium',
    name: 'Premium',
    tagline: 'Máxima potencia, zero límites',
    price: '$149/mes',
    priceValue: 149,
    priceLabel: 'Facturación mensual',
    yearlyPrice: 1490,
    yearlyLabel: '$1.490/año (2 meses gratis)',
    description: 'La experiencia completa con frecuencias premium, storage masivo, lives HD y soporte 24/7 dedicado.',
    storage: '1 TB',
    features: [
      'Todo lo de Business',
      'Frecuencias RFW especiales (sujetas a disponibilidad + pago + aprobación)',
      'Lives en HD sin límite',
      'Videos ilimitados',
      'Storage 1 TB',
      'App individual avanzada',
      'Soporte 24/7 dedicado',
      'White-label completo',
      'API avanzada + Webhooks',
    ],
    highlighted: false,
    badge: 'Profesional',
    color: 'accent',
    cta: 'Elegir Premium',
    requiresApproval: false,
    activationLabel: 'Activación inmediata',
    activationIcon: 'ri-flashlight-line',
    hasDomain: true,
    hasLives: true,
    hasApp: true,
  },
];

export interface ComparisonFeature {
  label: string;
  key: string;
  category: string;
  values: Record<string, string | boolean>;
}

export const comparisonFeatures: ComparisonFeature[] = [
  { label: 'Perfil profesional', key: 'profile', category: 'Perfil y presencia', values: { free: true, starter: true, pro: true, business: true, premium: true } },
  { label: 'Stream URL', key: 'stream', category: 'Perfil y presencia', values: { free: true, starter: true, pro: true, business: true, premium: true } },
  { label: 'Redes sociales', key: 'social', category: 'Perfil y presencia', values: { free: true, starter: true, pro: true, business: true, premium: true } },
  { label: 'Sección de contacto', key: 'contact', category: 'Perfil y presencia', values: { free: true, starter: true, pro: true, business: true, premium: true } },
  { label: 'Dominio personalizado', key: 'domain', category: 'Perfil y presencia', values: { free: false, starter: false, pro: true, business: true, premium: true } },
  { label: 'Cabina de chat', key: 'cabina', category: 'Interacción', values: { free: 'Básica', starter: 'Completa', pro: 'Completa', business: 'Completa', premium: 'Completa' } },
  { label: 'Comunidad de oyentes', key: 'community', category: 'Interacción', values: { free: false, starter: false, pro: true, business: true, premium: true } },
  { label: 'Publicaciones tipo Facebook', key: 'posts', category: 'Interacción', values: { free: false, starter: 'Blog simple', pro: true, business: true, premium: true } },
  { label: 'Fotos', key: 'photos', category: 'Multimedia', values: { free: false, starter: 'Hasta 500', pro: 'Ilimitadas', business: 'Ilimitadas', premium: 'Ilimitadas' } },
  { label: 'Galerías de fotos', key: 'galleries', category: 'Multimedia', values: { free: false, starter: '3 galerías', pro: true, business: true, premium: true } },
  { label: 'Videos', key: 'videos', category: 'Multimedia', values: { free: false, starter: false, pro: false, business: 'Ilimitados', premium: 'Ilimitados' } },
  { label: 'Lives / Transmisiones', key: 'lives', category: 'Multimedia', values: { free: false, starter: false, pro: false, business: 'HD estándar', premium: 'HD sin límite' } },
  { label: 'Almacenamiento', key: 'storage', category: 'Recursos', values: { free: '1 GB', starter: '10 GB', pro: '50 GB', business: '200 GB', premium: '1 TB' } },
  { label: 'Locutores', key: 'hosts', category: 'Recursos', values: { free: '1', starter: '2', pro: '5', business: 'Ilimitados', premium: 'Ilimitados' } },
  { label: 'Frecuencias RFW especiales', key: 'freq', category: 'Recursos', values: { free: false, starter: false, pro: false, business: false, premium: 'Disponible (sujeto a pago + aprobación)' } },
  { label: 'App individual', key: 'app', category: 'Recursos', values: { free: false, starter: false, pro: false, business: 'Básica', premium: 'Avanzada' } },
  { label: 'Activación', key: 'activation', category: 'Servicio', values: { free: 'Sujeto a aprobación', starter: 'Inmediata', pro: 'Inmediata', business: 'Inmediata', premium: 'Inmediata' } },
  { label: 'Estadísticas', key: 'stats', category: 'Servicio', values: { free: 'Básicas', starter: 'Básicas', pro: 'Avanzadas', business: 'Avanzadas', premium: 'Avanzadas' } },
  { label: 'Analytics', key: 'analytics', category: 'Servicio', values: { free: false, starter: false, pro: false, business: true, premium: true } },
  { label: 'Soporte', key: 'support', category: 'Servicio', values: { free: 'Comunidad', starter: 'Email', pro: 'Email', business: 'Prioritario', premium: '24/7 dedicado' } },
  { label: 'API de acceso', key: 'api', category: 'Servicio', values: { free: false, starter: false, pro: false, business: true, premium: 'API + Webhooks' } },
  { label: 'Sin publicidad', key: 'noads', category: 'Servicio', values: { free: false, starter: true, pro: true, business: true, premium: true } },
  { label: 'Licencia anual disponible', key: 'yearly', category: 'Servicio', values: { free: false, starter: '$120/año', pro: '$290/año', business: '$890/año', premium: '$1.490/año' } },
];

export const faqs = [
  {
    question: '¿Puedo cambiar de plan en cualquier momento?',
    answer: 'Sí. Podés hacer upgrade cuando quieras. El cambio es inmediato y solo pagás la diferencia proporcional del ciclo de facturación.',
  },
  {
    question: '¿Qué pasa si contrato un plan pago y no me gusta?',
    answer: 'Tenés 7 días de garantía. Si no estás conforme, te devolvemos el 100% de tu pago sin preguntas.',
  },
  {
    question: '¿El plan Free incluye publicidad?',
    answer: 'El plan Free no muestra publicidad de terceros en tu página de radio. La única limitación son las funcionalidades: sin fotos, videos, lives ni publicaciones.',
  },
  {
    question: '¿Cómo funciona la aprobación del plan Free?',
    answer: 'Cuando creás tu radio en plan Free, nuestro equipo revisa que la estación cumpla con los requisitos básicos: tener un stream activo, contenido original y datos de contacto verificables. El proceso suele tomar 24 a 48 horas.',
  },
  {
    question: '¿Qué son las frecuencias especiales o premium?',
    answer: 'Son Frecuencias RFW especiales como 94.70, 99.99, 100.00 o 103.50. Son fáciles de recordar y dan mayor visibilidad en el dial digital. Las Frecuencias RFW especiales están sujetas a disponibilidad, pago y aprobación del equipo. Se solicitan después del registro, desde el panel de administración.',
  },
  {
    question: '¿Puedo tener mi propio dominio con cualquier plan?',
    answer: 'El dominio personalizado está disponible desde el plan Pro en adelante. Los planes Free y Starter usan un subdominio de RadioFrecuenciaWeb.',
  },
  {
    question: '¿Qué incluye la app individual?',
    answer: 'La app individual es una aplicación móvil personalizada con el branding de tu radio, disponible en los planes Business (versión básica) y Premium (versión avanzada con notificaciones push y analytics).',
  },
  {
    question: '¿Conviene la licencia anual?',
    answer: 'Sí, si ya sabés que vas a estar al menos un año. La licencia anual te da 2 meses gratis respecto al precio mensual. Aplica desde el plan Starter en adelante.',
  },
];